import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase, getUserProfile, signOut as supabaseSignOut } from '../lib/supabase';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  
  // Ref containers for inactivity tracking
  const inactivityTimeoutRef = useRef(null);
  const userRef = useRef(user);

  // Keep userRef in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // 1. Downgrade expired users to free
  const downgradeToFree = useCallback(async (userId) => {
    try {
      const { data: updatedProfile, error: profileError } = await supabase
        .from('profiles')
        .update({ 
          subscription_plan: 'free',
          is_premium: false,
          premium_expires_at: null
        })
        .eq('id', userId)
        .select()
        .single();

      await supabase
        .from('subscriptions')
        .update({ status: 'expired' })
        .eq('user_id', userId)
        .eq('status', 'active');

      if (!profileError) {
        setProfile(updatedProfile);
        setSubscription(null);
        toast.info('Your premium subscription has expired');
      }
    } catch (error) {
      console.error('Error downgrading to free:', error);
    }
  }, []);

  // 2. Load both profile AND subscription data
  const loadUserData = useCallback(async (userId) => {
    setLoading(true);
    try {
      const profileData = await getUserProfile(userId);
      setProfile(profileData);

      if (profileData?.premium_expires_at) {
        const fallbackExpiry = new Date(profileData.premium_expires_at);
        if (fallbackExpiry < new Date() && profileData?.subscription_plan !== 'free') {
          await downgradeToFree(userId);
          return;
        }
      }

      const { data: subscriptionData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      const activeSub = subscriptionData?.[0] || null;

      if (!subError && activeSub) {
        setSubscription(activeSub);
        
        const endDate = new Date(activeSub.end_date);
        const now = new Date();
        
        if (endDate < now && profileData?.subscription_plan !== 'free') {
          await downgradeToFree(userId);
        }
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  }, [downgradeToFree]);

  // --- AUTH METHODS ---

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) throw error;
      if (data?.user && !data.session) {
        toast.success('Please check your email to confirm your account!');
        return { user: data.user, requiresConfirmation: true };
      }
      return { user: data.user, requiresConfirmation: false };
    } catch (error) {
      toast.error(error.message || 'Failed to create account');
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    } catch (error) {
      toast.error(error.message || 'Failed to login');
      throw error;
    }
  };

  const resendVerification = async (email) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`, 
        },
      });
      if (error) throw error;
      toast.success('A fresh activation link has been sent to your inbox!');
    } catch (error) {
      toast.error(error.message || 'Failed to resend confirmation email');
      throw error;
    }
  };

  const signOut = async () => {
    try {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      
      await supabaseSignOut();
      setUser(null);
      setProfile(null);
      setSubscription(null);
      setSession(null);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const refreshProfile = async () => {
    if (user) await loadUserData(user.id);
  };

  const updateProfile = async (updates) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      setProfile(data);
      return data;
    } catch (error) {
      toast.error('Error updating profile');
      throw error;
    }
  };

  // --- INACTIVITY AUTO-LOGOUT ENGINE ---
  
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    if (userRef.current) {
      const minutesBeforeLogout = 150; // 2.5 hours
      
      inactivityTimeoutRef.current = setTimeout(async () => {
        toast('Session expired due to inactivity.', { icon: '⏳' });
        await signOut();
      }, minutesBeforeLogout * 60 * 1000);
    }
  }, []);

  // Global Inactivity Event Listeners with Throttling
  useEffect(() => {
    if (!user) {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      return;
    }

    resetInactivityTimer();

    let lastExecution = Date.now();
    const handleUserActivity = () => {
      const now = Date.now();
      // Only reset timer at most once every 5 seconds during continuous activity
      if (now - lastExecution > 5000) {
        resetInactivityTimer();
        lastExecution = now;
      }
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [user, resetInactivityTimer]);

  // 3. Auth State Observer
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      
      if (currentUser) {
        loadUserData(currentUser.id);
      } else {
        setProfile(null);
        setSubscription(null);
        setLoading(false);
      }
    });

    return () => authSubscription.unsubscribe();
  }, [loadUserData]);

  // 4. Premium Check
  const isPremium = useMemo(() => {
    if (!profile?.subscription_plan) return false;
    
    if (profile.premium_expires_at) {
      const isExpired = new Date(profile.premium_expires_at) < new Date();
      if (isExpired) return false;
    }

    return profile.subscription_plan.toLowerCase() !== 'free';
  }, [profile]);

  const value = {
    user,
    profile,
    subscription,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resendVerification,
    refreshProfile,
    updateProfile,
    isAuthenticated: !!user,
    isPremium, 
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;